import { useCallback, useEffect, useState } from "react";
import { useAppSelector } from "@/store";
import { useNotification } from "@/context/NotificationContext";
import { extractErrorMessage } from "@/api/axios";
import * as commentsApi from "@/api/comments.api";
import { hr } from "@/constants/strings.hr";
import type { Comment } from "@/types";

export function useArticleComments(articleId: string) {
  const { isAuthenticated } = useAppSelector((s) => s.auth);
  const { notify } = useNotification();
  const [comments, setComments] = useState<Comment[]>([]);
  const [newComment, setNewComment] = useState("");
  const [submitting, setSubmitting] = useState(false);

  useEffect(() => {
    commentsApi.getComments(articleId).then(setComments);
  }, [articleId]);

  const submitComment = useCallback(async () => {
    if (!newComment.trim()) return;
    setSubmitting(true);
    try {
      const comment = await commentsApi.createComment(articleId, newComment);
      setComments((prev) => [comment, ...prev]);
      setNewComment("");
      notify(hr.comments.added, "success");
    } catch (err) {
      notify(extractErrorMessage(err), "error");
    } finally {
      setSubmitting(false);
    }
  }, [articleId, newComment, notify]);

  const replaceComment = useCallback((updated: Comment) => {
    setComments((prev) => prev.map((c) => (c.id === updated.id ? updated : c)));
  }, []);

  const removeComment = useCallback((id: string) => {
    setComments((prev) => prev.filter((c) => c.id !== id));
  }, []);

  return {
    comments,
    newComment,
    setNewComment,
    submitting,
    submitComment,
    isAuthenticated,
    replaceComment,
    removeComment,
  };
}
